<template>
  <div class="ar-shell">
    <base-dialog :show="!!error" title="An error occurred!" @close="error = null">
      <p>{{ error }}</p>
    </base-dialog>

    <!-- Header -->
    <header class="ar-header">
      <div>
        <div class="eyebrow">Reports</div>
        <h1>Admin Report</h1>
        <p class="subtitle">Aggregated pay-in / pay-out breakdown across vendors, branches, clients, and banks.</p>
      </div>
      <div class="header-actions">
        <button
          v-if="reportLoaded"
          type="button"
          class="btn btn-export"
          @click="exportToExcel"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="btn-icon">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          Export to Excel
        </button>
      </div>
    </header>

    <!-- Filter panel -->
    <section class="ar-filters">
      <!-- Date range -->
      <div class="filter-block">
        <div class="filter-block-label">Date Range</div>
        <div class="ar-row">
          <div class="chip-group">
            <button
              v-for="p in datePresets"
              :key="p.value"
              type="button"
              class="chip"
              :class="{ 'chip-active': activePreset === p.value }"
              @click="applyDatePreset(p.value)"
            >{{ p.label }}</button>
          </div>
          <div class="date-inputs">
            <label class="ar-inline-field">
              <span>From</span>
              <input
                type="date"
                v-model="startDate"
                @change="activePreset = 'custom'"
                class="ar-input ar-input-sm"
              />
            </label>
            <label class="ar-inline-field">
              <span>To</span>
              <input
                type="date"
                v-model="endDate"
                @change="activePreset = 'custom'"
                class="ar-input ar-input-sm"
              />
            </label>
          </div>
        </div>
      </div>

      <!-- Entity filters -->
      <div class="filter-block">
        <div class="filter-block-label">Filters</div>
        <div class="filter-grid">
          <div class="ar-field">
            <label>Vendor</label>
            <select
              v-model="filterVendor"
              @change="onVendorChanged"
              class="ar-input"
              :disabled="filterOptionsLoading || isAdminRole"
            >
              <option value="">All vendors</option>
              <option v-for="v in filterOptions.vendors" :key="v" :value="v">{{ v }}</option>
            </select>
          </div>
          <div class="ar-field">
            <label>Branch</label>
            <select
              v-model="filterBranch"
              @change="onBranchChanged"
              class="ar-input"
              :disabled="filterOptionsLoading || !filterVendor"
            >
              <option value="">All branches</option>
              <option v-for="b in filterOptions.branches" :key="b" :value="b">{{ b }}</option>
            </select>
          </div>
          <div class="ar-field">
            <label>Client</label>
            <select
              v-model="filterClient"
              class="ar-input"
              :disabled="filterOptionsLoading || !filterVendor"
            >
              <option value="">All clients</option>
              <option v-for="c in filterOptions.clients" :key="c" :value="c">{{ c }}</option>
            </select>
          </div>
          <div class="ar-field">
            <label>Bank</label>
            <select
              v-model="filterBank"
              class="ar-input"
              :disabled="filterOptionsLoading || !filterVendor"
            >
              <option value="">All banks</option>
              <option v-for="b in filterOptions.banks" :key="b" :value="b">{{ b }}</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Group by + generate -->
      <div class="filter-actions">
        <div class="ar-field ar-field-inline">
          <label>Group breakdown by</label>
          <select v-model="groupBy" class="ar-input">
            <option value="vendor">Vendor</option>
            <option value="branch">Branch</option>
            <option value="client">Client</option>
            <option value="bank">Bank</option>
          </select>
        </div>
        <div class="filter-actions-buttons">
          <button
            type="button"
            class="btn btn-ghost"
            @click="resetFilters"
          >Reset</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="isLoading"
            @click="generateReport"
          >
            <svg v-if="!isLoading" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" class="btn-icon">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span v-else class="btn-spinner"></span>
            {{ isLoading ? 'Generating…' : 'Generate' }}
          </button>
        </div>
      </div>
    </section>

    <!-- Loading -->
    <div v-if="isLoading" class="loading-block">
      <div class="loading-spinner"></div>
      <div class="loading-label">Generating report…</div>
    </div>

    <!-- Empty state (no report yet) -->
    <div v-else-if="!reportLoaded" class="empty-hero">
      <div class="empty-hero-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
          <path d="M3 3v18h18" stroke-linecap="round" />
          <path d="M7 15l4-4 4 4 5-5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>
      <div class="empty-hero-title">No report generated yet</div>
      <div class="empty-hero-sub">
        Pick your filters and click <strong>Generate</strong> to view the report.
      </div>
    </div>

    <!-- Report content -->
    <template v-else>
      <!-- Summary -->
      <section class="ar-section">
        <div class="section-head">
          <div class="section-title">Summary</div>
          <div class="section-sub">
            {{ startDate === endDate ? startDate : `${startDate} → ${endDate}` }}
          </div>
        </div>

        <div class="type-blocks">
          <div v-for="t in types" :key="`sum-${t.value}`" class="type-block">
            <div class="type-badge" :class="'type-' + t.value">
              <span class="type-dot"></span>
              {{ t.label }}
            </div>
            <div class="status-grid">
              <div
                v-for="s in t.statuses"
                :key="`stat-${t.value}-${s}`"
                class="status-card"
                :class="'status-' + s"
              >
                <div class="status-name">{{ s }}</div>
                <div class="status-amount">₹{{ fmt(summary[t.value][`${s}Amount`]) }}</div>
                <div class="status-count">
                  {{ (summary[t.value][`${s}Count`] || 0).toLocaleString() }}
                  {{ (summary[t.value][`${s}Count`] || 0) === 1 ? 'order' : 'orders' }}
                  <span v-if="totalCountFor(t.value) > 0" class="status-pct">· {{ pctFmt(pctOfTotal(t.value, s)) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Commission & Margin -->
      <section v-if="hasCommission" class="ar-section">
        <div class="section-head">
          <div class="section-title">Commission &amp; Margin</div>
          <div class="section-sub">
            Realized on approved transactions only. Margin = client commission − branch commission (what we make).
          </div>
        </div>

        <!-- Hero totals: the three headline numbers side by side -->
        <div class="comm-hero">
          <div class="comm-card comm-client">
            <div class="comm-label">Client Commission Charged</div>
            <div class="comm-value">₹{{ fmt(summary.commission.client) }}</div>
            <div class="comm-split">
              Pay-In ₹{{ fmt(summary.commission.payin.client) }}
              · Pay-Out ₹{{ fmt(summary.commission.payout.client) }}
            </div>
          </div>
          <div class="comm-op">−</div>
          <div class="comm-card comm-branch">
            <div class="comm-label">Branch Commission Paid</div>
            <div class="comm-value">₹{{ fmt(summary.commission.branch) }}</div>
            <div class="comm-split">
              Pay-In ₹{{ fmt(summary.commission.payin.branch) }}
              · Pay-Out ₹{{ fmt(summary.commission.payout.branch) }}
            </div>
          </div>
          <div class="comm-op">=</div>
          <div class="comm-card comm-margin">
            <div class="comm-label">Net Margin — what we make</div>
            <div class="comm-value" :class="marginClass(summary.commission.margin)">
              ₹{{ fmt(summary.commission.margin) }}
            </div>
            <div class="comm-split">
              Pay-In ₹{{ fmt(summary.commission.payin.margin) }}
              · Pay-Out ₹{{ fmt(summary.commission.payout.margin) }}
            </div>
          </div>
        </div>

        <!-- Per-entity commission -->
        <div v-if="breakdown.length" class="type-block">
          <div class="section-sub comm-table-cap">
            By {{ groupByLabel.toLowerCase() }}
            <span v-if="!commissionShown" class="comm-note">
              — branch commission only ({{ groupByLabel.toLowerCase() }} view)
            </span>
          </div>
          <div class="table-wrap">
            <table class="ar-table">
              <thead>
                <tr>
                  <th class="th-entity">{{ groupByLabel }}</th>
                  <th v-if="commissionShown" class="th-comm">Client Commission</th>
                  <th class="th-comm">Branch Commission</th>
                  <th v-if="commissionShown" class="th-comm">Margin</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in breakdown" :key="`comm-${row.entity}`">
                  <td class="td-entity">{{ row.entity }}</td>
                  <td v-if="commissionShown" class="td-amount">
                    ₹{{ fmt(row.commission && row.commission.client) }}
                  </td>
                  <td class="td-amount">
                    ₹{{ fmt(row.commission && row.commission.branch) }}
                  </td>
                  <td
                    v-if="commissionShown"
                    class="td-amount"
                    :class="marginClass(row.commission && row.commission.margin)"
                  >
                    ₹{{ fmt(row.commission && row.commission.margin) }}
                  </td>
                </tr>
                <tr class="comm-total-row">
                  <td class="td-entity">TOTAL</td>
                  <td v-if="commissionShown" class="td-amount">₹{{ fmt(summary.commission.client) }}</td>
                  <td class="td-amount">₹{{ fmt(summary.commission.branch) }}</td>
                  <td
                    v-if="commissionShown"
                    class="td-amount"
                    :class="marginClass(summary.commission.margin)"
                  >₹{{ fmt(summary.commission.margin) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- Breakdown -->
      <section class="ar-section">
        <div class="section-head">
          <div class="section-title">
            Breakdown by {{ groupByLabel }}
            <span class="head-count">{{ breakdown.length }} {{ breakdown.length === 1 ? 'row' : 'rows' }}</span>
          </div>
          <div class="section-sub">Per-{{ groupByLabel.toLowerCase() }} totals across every status.</div>
        </div>

        <div v-if="breakdown.length === 0" class="empty-mini">
          No data for the selected filters.
        </div>

        <div v-else class="type-blocks">
          <div v-for="t in types" :key="`bk-${t.value}`" class="type-block">
            <div class="type-badge" :class="'type-' + t.value">
              <span class="type-dot"></span>
              {{ t.label }}
            </div>
            <div class="table-wrap">
              <table class="ar-table">
                <thead>
                  <tr>
                    <th class="th-entity" rowspan="2">{{ groupByLabel }}</th>
                    <th
                      v-for="s in t.statuses"
                      :key="`bh-${t.value}-${s}`"
                      colspan="2"
                      class="th-status"
                      :class="'th-status-' + s"
                    >{{ s }}</th>
                  </tr>
                  <tr class="tr-sub">
                    <template v-for="s in t.statuses" :key="`bsh-${t.value}-${s}`">
                      <th class="th-sub th-sub-amount">Amount</th>
                      <th class="th-sub th-sub-count">Count</th>
                    </template>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in breakdown" :key="`${t.value}-${row.entity}`">
                    <td class="td-entity">{{ row.entity }}</td>
                    <template v-for="s in t.statuses" :key="`bd-${t.value}-${row.entity}-${s}`">
                      <td class="td-amount">₹{{ fmt(row[t.value][`${s}Amount`]) }}</td>
                      <td class="td-count">
                        {{ (row[t.value][`${s}Count`] || 0).toLocaleString() }}
                        <span
                          v-if="totalCountForRow(row, t.value) > 0"
                          class="td-pct"
                        >{{ pctFmt(pctOfRowTotal(row, t.value, s)) }}</span>
                      </td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import http from "../../http-common";
import moment from "moment-timezone";
import xlsx from "xlsx/dist/xlsx.full.min.js";

export default {
  name: "admin-report",
  data() {
    // Compute "today" in the configured display TZ (IST) so the default
    // date range is correct between IST midnight and UTC midnight (5:30 AM IST).
    const today = moment().tz(process.env.VUE_APP_TIMEZONE || "Asia/Kolkata").format("YYYY-MM-DD");
    return {
      isLoading: false,
      filterOptionsLoading: false,
      reportLoaded: false,
      error: null,

      // Filters
      startDate: today,
      endDate: today,
      activePreset: "today",
      datePresets: [
        { value: "today", label: "Today" },
        { value: "yesterday", label: "Yesterday" },
        { value: "last7", label: "Last 7 days" },
        { value: "last30", label: "Last 30 days" },
        { value: "thisMonth", label: "This Month" },
        { value: "custom", label: "Custom" },
      ],
      filterVendor: "",
      filterBranch: "",
      filterClient: "",
      filterBank: "",
      groupBy: "vendor",
      // groupBy of the report actually rendered (set on Generate). Drives the
      // commission columns so header + data can't disagree if the user changes
      // the selector without regenerating.
      reportGroupBy: "vendor",

      filterOptions: { vendors: [], branches: [], clients: [], banks: [] },

      // Per-type status shape — matches backend `STATUSES_BY_TYPE`.
      // Pay-in has no `unassigned`, uses `failed` instead of `rejected`.
      // Pay-out has no `expired` (no expire cron for payouts) and no
      // `rejected` (rejectPayoutOrder writes `unassigned`;
      // vendorRejectPayout writes `failed`).
      types: [
        { value: "payin",  label: "Pay-In",  statuses: ["approved", "pending", "failed", "expired"] },
        { value: "payout", label: "Pay-Out", statuses: ["approved", "pending", "unassigned", "failed"] },
      ],

      // Results
      summary: this.makeEmptyTypes(),
      breakdown: [],
    };
  },
  computed: {
    isAdminRole() {
      return this.$store.getters.role === "admin";
    },
    isSuperAdmin() {
      return this.$store.getters.role === "superadmin";
    },
    groupByLabel() {
      const map = { vendor: "Vendor", branch: "Branch", client: "Client", bank: "Bank" };
      return map[this.groupBy] || this.groupBy;
    },
    // Client-side commission (and therefore margin) only makes sense when the
    // breakdown is grouped by an entity we CHARGE — vendor or client. For
    // branch/bank groupBy the backend nulls client/margin and we show only the
    // branch commission column. Keyed off the rendered report's groupBy.
    commissionShown() {
      return this.reportGroupBy === "vendor" || this.reportGroupBy === "client";
    },
    // Whether the payload carried a commission block (backend may be older).
    hasCommission() {
      return !!(this.summary && this.summary.commission);
    },
  },
  created() {
    if (this.isAdminRole) {
      // Force the admin's own vendor selection on the dropdown.
      this.filterVendor = this.$store.getters.vendor || "";
    }
    this.loadFilterOptions();
  },
  methods: {
    makeEmptyTypes() {
      const empty = (statuses) => {
        const o = {};
        for (const s of statuses) {
          o[`${s}Count`] = 0;
          o[`${s}Amount`] = 0;
        }
        return o;
      };
      // Mirror backend STATUSES_BY_TYPE so 0-filled defaults match the shape
      // the payload will overwrite.
      return {
        payin:  empty(["approved", "pending", "failed", "expired"]),
        payout: empty(["approved", "pending", "unassigned", "failed"]),
      };
    },
    fmt(val) {
      if (val === undefined || val === null) return 0;
      const n = Number(val);
      if (Number.isNaN(n)) return 0;
      return Math.round(n).toLocaleString();
    },
    // Total ORDER COUNT across every status for a given type in the
    // current summary. Denominator for the per-status percentage shown on
    // the summary status cards (per user request — percentage is computed
    // against this grand total, NOT against per-client subtotals).
    totalCountFor(typeValue) {
      const type = this.types.find((t) => t.value === typeValue);
      if (!type) return 0;
      const bucket = this.summary[typeValue] || {};
      return type.statuses.reduce(
        (sum, s) => sum + (Number(bucket[`${s}Count`]) || 0),
        0
      );
    },
    pctOfTotal(typeValue, status) {
      const count = Number((this.summary[typeValue] || {})[`${status}Count`]) || 0;
      const total = this.totalCountFor(typeValue);
      if (!total) return 0;
      return (count / total) * 100;
    },
    // Row-level denominator: sum of a single breakdown row's status counts
    // for that type. Used by the breakdown table so each row's percentages
    // sum to 100% within that row (per user: "group-by total level" —
    // if Client A has 30 total and 15 approved, show 50%, not the % of
    // all pay-in orders across every group).
    totalCountForRow(row, typeValue) {
      const type = this.types.find((t) => t.value === typeValue);
      if (!type) return 0;
      const bucket = (row && row[typeValue]) || {};
      return type.statuses.reduce(
        (sum, s) => sum + (Number(bucket[`${s}Count`]) || 0),
        0
      );
    },
    pctOfRowTotal(row, typeValue, status) {
      const total = this.totalCountForRow(row, typeValue);
      if (!total) return 0;
      const count = Number((row[typeValue] || {})[`${status}Count`]) || 0;
      return (count / total) * 100;
    },
    pctFmt(n) {
      const val = Number(n) || 0;
      // 1 decimal for anything < 100% (so 74.1%, 0.5%); no decimal at 0% / 100%.
      if (val === 0 || val === 100) return val.toFixed(0) + "%";
      return val.toFixed(1) + "%";
    },
    // Colour the margin figure: green when we make money, red if it goes
    // negative (branch rate above client rate — a config error worth seeing).
    marginClass(v) {
      const n = Number(v) || 0;
      if (n < 0) return "margin-neg";
      if (n > 0) return "margin-pos";
      return "";
    },
    applyDatePreset(preset) {
      this.activePreset = preset;
      // All preset math runs in the configured display TZ (IST) so the date
      // boundaries align with IST midnight, not UTC midnight.
      const tz = process.env.VUE_APP_TIMEZONE || "Asia/Kolkata";
      const today = moment().tz(tz);
      if (preset === "today") {
        this.startDate = today.format("YYYY-MM-DD");
        this.endDate = today.format("YYYY-MM-DD");
      } else if (preset === "yesterday") {
        const y = moment().tz(tz).subtract(1, "days");
        this.startDate = y.format("YYYY-MM-DD");
        this.endDate = y.format("YYYY-MM-DD");
      } else if (preset === "last7") {
        this.startDate = moment().tz(tz).subtract(6, "days").format("YYYY-MM-DD");
        this.endDate = today.format("YYYY-MM-DD");
      } else if (preset === "last30") {
        this.startDate = moment().tz(tz).subtract(29, "days").format("YYYY-MM-DD");
        this.endDate = today.format("YYYY-MM-DD");
      } else if (preset === "thisMonth") {
        this.startDate = moment().tz(tz).startOf("month").format("YYYY-MM-DD");
        this.endDate = today.format("YYYY-MM-DD");
      }
      // 'custom' doesn't touch the dates
    },
    async loadFilterOptions() {
      this.filterOptionsLoading = true;
      try {
        const params = {};
        if (this.filterVendor) params.vendor = this.filterVendor;
        if (this.filterBranch) params.branch = this.filterBranch;
        const response = await http.get("/reports/admin-report-filters", { params });
        if (response.data && response.data.success) {
          this.filterOptions = response.data.data;
        }
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) || e.message || "Failed to load filter options";
      }
      this.filterOptionsLoading = false;
    },
    async onVendorChanged() {
      // Cascading: vendor change resets branch/client/bank and reloads dropdowns.
      this.filterBranch = "";
      this.filterClient = "";
      this.filterBank = "";
      await this.loadFilterOptions();
    },
    async onBranchChanged() {
      // Cascading: branch change resets bank only (clients are vendor-scoped, not branch-scoped).
      this.filterBank = "";
      await this.loadFilterOptions();
    },
    resetFilters() {
      this.filterVendor = this.isAdminRole ? this.$store.getters.vendor || "" : "";
      this.filterBranch = "";
      this.filterClient = "";
      this.filterBank = "";
      this.groupBy = "vendor";
      this.applyDatePreset("today");
      this.reportLoaded = false;
      this.summary = this.makeEmptyTypes();
      this.breakdown = [];
      this.loadFilterOptions();
    },
    async generateReport() {
      if (!this.startDate || !this.endDate) {
        this.error = "Please pick a valid date range.";
        return;
      }
      if (moment(this.startDate).isAfter(moment(this.endDate))) {
        this.error = "Start date cannot be after end date.";
        return;
      }
      this.isLoading = true;
      try {
        const params = {
          startDate: this.startDate,
          endDate: this.endDate,
          groupBy: this.groupBy,
        };
        if (this.filterVendor) params.vendor = this.filterVendor;
        if (this.filterBranch) params.branch = this.filterBranch;
        if (this.filterClient) params.client = this.filterClient;
        if (this.filterBank) params.bank = this.filterBank;

        const response = await http.get("/reports/admin-report", { params });
        if (response.data && response.data.success) {
          this.summary = response.data.summary || this.makeEmptyTypes();
          this.breakdown = response.data.breakdown || [];
          this.reportGroupBy =
            (response.data.filters && response.data.filters.groupBy) || this.groupBy;
          this.reportLoaded = true;
        } else {
          this.error = (response.data && response.data.message) || "Failed to generate report";
        }
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.message) || e.message || "Failed to generate report";
      }
      this.isLoading = false;
    },
    exportToExcel() {
      try {
        const wb = xlsx.utils.book_new();
        const tz = process.env.VUE_APP_TIMEZONE || "Asia/Kolkata";
        const generatedAt = moment().tz(tz).format("YYYY-MM-DD HH:mm:ss");

        // ------------- Sheet 1: Overview (metadata + summary) -------------
        //
        // Layout:
        //   Report header + filters (rows 1-8)
        //   For each type: label row, header row, one row per status,
        //   Total row, blank row.
        //
        // Number formats: amounts render as ₹-prefixed with thousands
        // separators; percentages render as 74.1% (stored as decimals so
        // Excel keeps the math correct if the user re-computes anything).
        const overviewAoa = [];
        overviewAoa.push(["ADMIN REPORT"]);
        overviewAoa.push(["Date Range", `${this.startDate} to ${this.endDate}`]);
        overviewAoa.push(["Group By", this.groupByLabel]);
        overviewAoa.push(["Vendor", this.filterVendor || "(all)"]);
        overviewAoa.push(["Branch", this.filterBranch || "(all)"]);
        overviewAoa.push(["Client", this.filterClient || "(all)"]);
        overviewAoa.push(["Bank",   this.filterBank   || "(all)"]);
        overviewAoa.push(["Generated (IST)", generatedAt]);
        overviewAoa.push([]);

        const overviewFormats = {}; // cellAddr -> excel format string

        for (const t of this.types) {
          // Compute type totals from the summary bucket.
          let totalCount = 0;
          let totalAmount = 0;
          for (const s of t.statuses) {
            totalCount  += Number(this.summary[t.value][`${s}Count`]  || 0);
            totalAmount += Number(this.summary[t.value][`${s}Amount`] || 0);
          }

          overviewAoa.push([`${t.label.toUpperCase()} — TOTALS`]);
          overviewAoa.push(["Status", "Amount", "Count", "Share"]);
          const headerRow = overviewAoa.length; // 1-based row for xlsx

          for (const s of t.statuses) {
            const c = Number(this.summary[t.value][`${s}Count`]  || 0);
            const a = Number(this.summary[t.value][`${s}Amount`] || 0);
            const pct = totalCount ? c / totalCount : 0;
            overviewAoa.push([s, a, c, pct]);
            const r = overviewAoa.length; // 1-based
            overviewFormats[xlsx.utils.encode_cell({ c: 1, r: r - 1 })] = '"₹"#,##0';
            overviewFormats[xlsx.utils.encode_cell({ c: 3, r: r - 1 })] = "0.0%";
          }
          overviewAoa.push(["Total", totalAmount, totalCount, totalCount ? 1 : 0]);
          const totalR = overviewAoa.length;
          overviewFormats[xlsx.utils.encode_cell({ c: 1, r: totalR - 1 })] = '"₹"#,##0';
          overviewFormats[xlsx.utils.encode_cell({ c: 3, r: totalR - 1 })] = "0.0%";
          overviewAoa.push([]);
          void headerRow;
        }

        // Commission & Margin totals block.
        if (this.summary.commission) {
          const cm = this.summary.commission;
          overviewAoa.push(["COMMISSION & MARGIN (approved only)"]);
          overviewAoa.push(["Metric", "Pay-In", "Pay-Out", "Total"]);
          const commRows = [
            ["Client Commission Charged", cm.payin.client, cm.payout.client, cm.client],
            ["Branch Commission Paid", cm.payin.branch, cm.payout.branch, cm.branch],
            ["Net Margin (what we make)", cm.payin.margin, cm.payout.margin, cm.margin],
          ];
          for (const cr of commRows) {
            overviewAoa.push(cr);
            const r = overviewAoa.length;
            for (const c of [1, 2, 3]) {
              overviewFormats[xlsx.utils.encode_cell({ c, r: r - 1 })] = '"₹"#,##0';
            }
          }
          overviewAoa.push([]);
        }

        const wsOverview = xlsx.utils.aoa_to_sheet(overviewAoa);
        wsOverview["!cols"] = [
          { wch: 22 }, { wch: 18 }, { wch: 12 }, { wch: 10 },
        ];
        for (const addr in overviewFormats) {
          if (wsOverview[addr]) wsOverview[addr].z = overviewFormats[addr];
        }
        xlsx.utils.book_append_sheet(wb, wsOverview, "Overview");

        // ------------- Sheets 2 + 3: Breakdown per type -------------
        //
        // Layout per type sheet:
        //   Header block (title, date range, filters — 4 rows)
        //   Blank row
        //   Two-row column header:
        //     Row A: entity | STATUS (merged over Amount/Count/Share) x N | ROW TOTALS
        //     Row B: entity | Amount | Count | Share | ... | Count | Share of Type
        //   Data rows (one per entity)
        //   Grand-total row summing every column
        //
        // Percentages within a row are that row's own denominator (matches
        // the on-screen breakdown). Extra "Share of Type" column shows the
        // row's share of the type's grand total (from summary).
        for (const t of this.types) {
          const aoa = [];
          aoa.push([`${t.label.toUpperCase()} BREAKDOWN`]);
          aoa.push(["Date Range", `${this.startDate} to ${this.endDate}`]);
          aoa.push(["Group By", this.groupByLabel]);
          aoa.push(["Filters", [
            this.filterVendor && `Vendor=${this.filterVendor}`,
            this.filterBranch && `Branch=${this.filterBranch}`,
            this.filterClient && `Client=${this.filterClient}`,
            this.filterBank   && `Bank=${this.filterBank}`,
          ].filter(Boolean).join(" · ") || "(none)"]);
          aoa.push([]);

          // Two-row column header
          const headerRowA = [this.groupByLabel];
          const headerRowB = [""];
          const merges = [];
          const headerRowAIdx = aoa.length; // 0-based index where this row lands after push
          for (let i = 0; i < t.statuses.length; i++) {
            const s = t.statuses[i];
            headerRowA.push(s.toUpperCase(), null, null); // 3 cols per status
            headerRowB.push("Amount", "Count", "Share");
            // Merge the 3 cells above each status
            const startCol = 1 + i * 3;
            merges.push({
              s: { c: startCol,     r: headerRowAIdx },
              e: { c: startCol + 2, r: headerRowAIdx },
            });
          }
          // Row-total columns
          headerRowA.push("ROW TOTALS", null);
          headerRowB.push("Count", "% of type");
          const totalStartCol = 1 + t.statuses.length * 3;
          merges.push({
            s: { c: totalStartCol,     r: headerRowAIdx },
            e: { c: totalStartCol + 1, r: headerRowAIdx },
          });
          aoa.push(headerRowA);
          aoa.push(headerRowB);

          // Compute type grand total for the "% of type" column
          const grandCount = this.totalCountFor(t.value);

          // Data rows
          const cellFormats = {}; // cellAddr → format
          const dataStartRow = aoa.length; // 0-based index of first data row
          for (const entry of this.breakdown) {
            const row = [entry.entity];
            let rowCount = 0;
            for (const s of t.statuses) {
              const c = Number(entry[t.value][`${s}Count`]  || 0);
              const a = Number(entry[t.value][`${s}Amount`] || 0);
              rowCount += c;
              row.push(a, c, 0); // pct backfilled after we know rowCount
            }
            // Now backfill percentages (relative to row's own count).
            for (let i = 0; i < t.statuses.length; i++) {
              const c = row[1 + i * 3 + 1];
              row[1 + i * 3 + 2] = rowCount ? c / rowCount : 0;
            }
            const rowShareOfType = grandCount ? rowCount / grandCount : 0;
            row.push(rowCount, rowShareOfType);
            aoa.push(row);
            const r = aoa.length - 1;
            // Formats for this data row
            for (let i = 0; i < t.statuses.length; i++) {
              const startCol = 1 + i * 3;
              cellFormats[xlsx.utils.encode_cell({ c: startCol,     r })] = '"₹"#,##0';
              cellFormats[xlsx.utils.encode_cell({ c: startCol + 2, r })] = "0.0%";
            }
            cellFormats[xlsx.utils.encode_cell({ c: totalStartCol + 1, r })] = "0.0%";
          }

          // Grand-total row across all entities
          if (this.breakdown.length > 0) {
            const totals = [`TOTAL (${this.breakdown.length} ${this.groupByLabel.toLowerCase()}${this.breakdown.length === 1 ? "" : "s"})`];
            let sumRowCount = 0;
            for (const s of t.statuses) {
              const c = this.breakdown.reduce((sum, e) => sum + Number(e[t.value][`${s}Count`]  || 0), 0);
              const a = this.breakdown.reduce((sum, e) => sum + Number(e[t.value][`${s}Amount`] || 0), 0);
              sumRowCount += c;
              totals.push(a, c, 0); // backfill
            }
            for (let i = 0; i < t.statuses.length; i++) {
              const c = totals[1 + i * 3 + 1];
              totals[1 + i * 3 + 2] = sumRowCount ? c / sumRowCount : 0;
            }
            totals.push(sumRowCount, grandCount ? sumRowCount / grandCount : 0);
            aoa.push(totals);
            const r = aoa.length - 1;
            for (let i = 0; i < t.statuses.length; i++) {
              const startCol = 1 + i * 3;
              cellFormats[xlsx.utils.encode_cell({ c: startCol,     r })] = '"₹"#,##0';
              cellFormats[xlsx.utils.encode_cell({ c: startCol + 2, r })] = "0.0%";
            }
            cellFormats[xlsx.utils.encode_cell({ c: totalStartCol + 1, r })] = "0.0%";
          }
          void dataStartRow;

          const ws = xlsx.utils.aoa_to_sheet(aoa);
          ws["!merges"] = merges;
          const cols = [{ wch: 24 }]; // entity
          for (let i = 0; i < t.statuses.length; i++) {
            cols.push({ wch: 14 }, { wch: 8 }, { wch: 9 });
          }
          cols.push({ wch: 12 }, { wch: 12 }); // row totals
          ws["!cols"] = cols;
          for (const addr in cellFormats) {
            if (ws[addr]) ws[addr].z = cellFormats[addr];
          }
          xlsx.utils.book_append_sheet(wb, ws, `${t.label} Breakdown`);
        }

        // ------------- Commission & Margin sheet -------------
        // Per-entity commission. Client + Margin columns only for vendor/client
        // groupBy (branch/bank rows carry only branch commission — mirrors the
        // on-screen rule). Total row comes from the summary so it matches the
        // hero figures exactly.
        if (this.summary.commission) {
          const cm = this.summary.commission;
          const showClient = this.commissionShown;
          const caoa = [];
          caoa.push(["COMMISSION & MARGIN"]);
          caoa.push(["Date Range", `${this.startDate} to ${this.endDate}`]);
          caoa.push(["Group By", this.groupByLabel]);
          caoa.push(["Basis", "Approved transactions only · Margin = Client − Branch"]);
          caoa.push([]);

          const header = [this.groupByLabel];
          if (showClient) header.push("Client Commission");
          header.push("Branch Commission");
          if (showClient) header.push("Margin");
          caoa.push(header);

          const cfmt = {};
          for (const entry of this.breakdown) {
            const c = entry.commission || {};
            const row = [entry.entity];
            if (showClient) row.push(Number(c.client) || 0);
            row.push(Number(c.branch) || 0);
            if (showClient) row.push(Number(c.margin) || 0);
            caoa.push(row);
            const r = caoa.length - 1;
            for (let i = 1; i < row.length; i++) {
              cfmt[xlsx.utils.encode_cell({ c: i, r })] = '"₹"#,##0';
            }
          }

          const total = ["TOTAL"];
          if (showClient) total.push(Number(cm.client) || 0);
          total.push(Number(cm.branch) || 0);
          if (showClient) total.push(Number(cm.margin) || 0);
          caoa.push(total);
          const tr = caoa.length - 1;
          for (let i = 1; i < total.length; i++) {
            cfmt[xlsx.utils.encode_cell({ c: i, r: tr })] = '"₹"#,##0';
          }

          const cws = xlsx.utils.aoa_to_sheet(caoa);
          const ccols = [{ wch: 24 }];
          for (let i = 1; i < header.length; i++) ccols.push({ wch: 18 });
          cws["!cols"] = ccols;
          for (const addr in cfmt) {
            if (cws[addr]) cws[addr].z = cfmt[addr];
          }
          xlsx.utils.book_append_sheet(wb, cws, "Commission & Margin");
        }

        const fileName = `admin-report-${this.startDate}-to-${this.endDate}.xlsx`;
        xlsx.writeFile(wb, fileName);
      } catch (e) {
        this.error = "Excel export failed: " + (e.message || "unknown error");
      }
    },
  },
};
</script>

<style scoped>
/* ==================== Shell ==================== */
.ar-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 20px;
  background: linear-gradient(180deg, #fbfaf7 0%, #f4f0e5 100%);
  overflow-y: auto;
}

/* ==================== Header ==================== */
.ar-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #1B2A47;
  font-weight: 600;
  margin-bottom: 4px;
}
.ar-header h1 {
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.1;
  margin: 0;
}
.subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0;
}

/* ==================== Buttons ==================== */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  outline: none;
}
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-primary {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 6px 16px -6px rgba(27, 42, 71, 0.5);
}
.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px -8px rgba(27, 42, 71, 0.55);
}
.btn-ghost {
  background: #fff;
  color: #475569;
  border-color: #e2e8f0;
}
.btn-ghost:hover:not(:disabled) { background: #f8fafc; border-color: #cbd5e1; }
.btn-export {
  background: #ecfdf5;
  color: #047857;
  border-color: #a7f3d0;
}
.btn-export:hover:not(:disabled) {
  background: #d1fae5;
  border-color: #6ee7b7;
}
.btn-icon { width: 15px; height: 15px; }
.btn-spinner {
  width: 14px; height: 14px;
  border-radius: 999px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ==================== Filter panel ==================== */
.ar-filters {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.filter-block { display: flex; flex-direction: column; gap: 10px; }
.filter-block-label {
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.ar-row {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}
.chip-group { display: flex; gap: 4px; flex-wrap: wrap; }
.chip {
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 12px;
  color: #475569;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.12s;
}
.chip:hover { border-color: #cbd5e1; background: #f8fafc; }
.chip-active {
  background: #eef2f7;
  border-color: #cbd5e1;
  color: #1B2A47;
  font-weight: 600;
}
.date-inputs { display: flex; gap: 10px; flex-wrap: wrap; }
.ar-inline-field {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #64748b;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px 16px;
}
@media (max-width: 900px) { .filter-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 520px) { .filter-grid { grid-template-columns: 1fr; } }
.ar-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.ar-field label {
  font-size: 10px;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.filter-actions {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  padding-top: 6px;
  border-top: 1px solid #f1f5f9;
  flex-wrap: wrap;
}
.ar-field-inline { min-width: 200px; }
.filter-actions-buttons { display: flex; gap: 8px; }

/* ==================== Inputs ==================== */
.ar-input {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #f8fafc;
  outline: none;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  font-family: inherit;
  color: #0f172a;
}
.ar-input:focus {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(27, 42, 71, 0.14);
}
.ar-input:disabled {
  cursor: not-allowed;
  opacity: 0.6;
  background: #f1f5f9;
}
.ar-input-sm { padding: 7px 10px; font-size: 12px; width: auto; }
select.ar-input {
  appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' d='M1 1l5 5 5-5'/></svg>");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 32px;
}

/* ==================== Loading + empty states ==================== */
.loading-block {
  padding: 60px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
}
.loading-spinner {
  width: 44px; height: 44px;
  border-radius: 999px;
  border: 3px solid #e2e8f0;
  border-top-color: #1B2A47;
  animation: spin 0.9s linear infinite;
}
.loading-label {
  font-size: 14px;
  font-weight: 600;
  color: #475569;
}

.empty-hero {
  background: #fff;
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  padding: 60px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
}
.empty-hero-icon {
  width: 60px; height: 60px;
  border-radius: 16px;
  background: #eef2f7;
  color: #1B2A47;
  display: flex; align-items: center; justify-content: center;
}
.empty-hero-icon svg { width: 30px; height: 30px; }
.empty-hero-title { font-size: 16px; font-weight: 600; color: #0f172a; }
.empty-hero-sub { font-size: 13px; color: #64748b; }
.empty-hero-sub strong { color: #1B2A47; }

.empty-mini {
  background: #fff;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  font-size: 13px;
  color: #64748b;
}

/* ==================== Section ==================== */
.ar-section {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.section-head {}
.section-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.head-count {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 999px;
  letter-spacing: 0;
}
.section-sub {
  font-size: 12px;
  color: #64748b;
  margin-top: 3px;
}

/* ==================== Type blocks (Pay-In / Pay-Out) ==================== */
.type-blocks {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.type-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.type-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  align-self: flex-start;
}
.type-payin  { background: #eef2f7; color: #1B2A47; }
.type-payout { background: #ecfdf5; color: #047857; }
.type-dot {
  width: 6px; height: 6px;
  border-radius: 999px;
  display: inline-block;
}
.type-payin  .type-dot { background: #1B2A47; }
.type-payout .type-dot { background: #10b981; }

/* ==================== Summary status grid ==================== */
/* auto-fit + minmax keeps cards evenly-distributed regardless of how many
   statuses the type declares (4 today, could be N tomorrow). No empty
   slot when a type has fewer statuses than the widest one. */
.status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 10px;
}
.status-card {
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  padding: 10px 12px;
  border-left-width: 3px;
}
.status-approved   { border-left-color: #10b981; }
.status-pending    { border-left-color: #f59e0b; }
.status-rejected   { border-left-color: #ef4444; }
.status-failed     { border-left-color: #ef4444; }
.status-unassigned { border-left-color: #64748b; }
.status-expired    { border-left-color: #fb923c; }
.status-name {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #64748b;
  text-transform: uppercase;
}
/* Amount is the headline number — big, bold, tabular. */
.status-amount {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  margin-top: 4px;
  font-variant-numeric: tabular-nums;
  line-height: 1.15;
  letter-spacing: -0.01em;
}
/* Count moves to secondary — small, muted, with unit suffix ("orders")
   and the share-of-total percentage. */
.status-count {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 3px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  flex-wrap: wrap;
}
.status-pct {
  color: #1B2A47;
  font-weight: 600;
}

/* ==================== Breakdown table ==================== */
.table-wrap {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  overflow-x: auto;
}
.ar-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.ar-table thead {
  background: #f8fafc;
}
.ar-table th {
  padding: 8px 10px;
  border-bottom: 1px solid #e2e8f0;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 11px;
  text-align: center;
  white-space: nowrap;
}
.th-entity {
  text-align: left;
  border-right: 1px solid #e2e8f0;
  min-width: 140px;
}
.th-status-approved   { color: #047857; }
.th-status-pending    { color: #b45309; }
.th-status-rejected   { color: #b91c1c; }
.th-status-failed     { color: #b91c1c; }
.th-status-unassigned { color: #475569; }
.th-status-expired    { color: #c2410c; }
.tr-sub .th-sub {
  font-size: 10px;
  padding: 6px 10px;
  color: #94a3b8;
  border-bottom: 1px solid #e2e8f0;
  background: #f1f5f9;
}
/* Amount column: emphasized. Count column: de-emphasized. */
.tr-sub .th-sub-amount { color: #475569; font-weight: 700; }
.tr-sub .th-sub-count  { color: #94a3b8; font-weight: 500; }

.ar-table tbody tr {
  transition: background 0.12s;
}
.ar-table tbody tr:nth-child(even) { background: #fafbfc; }
.ar-table tbody tr:hover { background: #eef2f7; }
.ar-table td {
  padding: 10px;
  border-bottom: 1px solid #f1f5f9;
  text-align: center;
  font-variant-numeric: tabular-nums;
  color: #0f172a;
}
.ar-table tbody tr:last-child td { border-bottom: none; }
.td-entity {
  text-align: left;
  font-weight: 600;
  color: #0f172a;
  border-right: 1px solid #f1f5f9;
  white-space: nowrap;
}
/* Amount cell — highlighted (bold, dark, right-aligned, tabular). */
.td-amount {
  text-align: right;
  padding-right: 14px;
  font-weight: 700;
  color: #0f172a;
  font-family: 'SFMono-Regular', Menlo, monospace;
  font-size: 12.5px;
}
/* Count cell — muted so amount reads first. */
.td-count {
  text-align: left;
  padding-left: 8px;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}
/* Share-of-total-orders percentage next to the count. Denominator is the
   type's grand total (from summary), NOT the row-local total. */
.td-pct {
  color: #1B2A47;
  font-weight: 600;
  font-size: 10px;
  margin-left: 2px;
}

/* ==================== Commission & Margin ==================== */
.comm-hero {
  display: flex;
  align-items: stretch;
  gap: 12px;
  flex-wrap: wrap;
}
.comm-card {
  flex: 1 1 220px;
  min-width: 200px;
  border-radius: 14px;
  padding: 16px 18px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.comm-op {
  display: flex;
  align-items: center;
  font-size: 24px;
  font-weight: 700;
  color: #94a3b8;
}
@media (max-width: 720px) { .comm-op { display: none; } }
.comm-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
}
.comm-value {
  font-size: 26px;
  font-weight: 800;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  line-height: 1.1;
}
.comm-split {
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}
.comm-client { border-left: 3px solid #1B2A47; }
.comm-branch { border-left: 3px solid #f59e0b; }
.comm-margin {
  border-left: 3px solid #10b981;
  background: #f0fdf4;
  border-color: #bbf7d0;
}
.margin-pos { color: #047857 !important; }
.margin-neg { color: #b91c1c !important; }

.comm-table-cap { margin-bottom: 8px; }
.comm-note { color: #94a3b8; font-weight: 500; }
.th-comm {
  text-align: right;
  padding-right: 14px !important;
  color: #475569;
  font-weight: 700;
}
.comm-total-row td {
  border-top: 2px solid #e2e8f0;
  font-weight: 800;
  background: #f8fafc;
}
.comm-total-row .td-entity { color: #0f172a; }
</style>
